<script setup>
// AIVIO timers: one action of a timer rule (type select + the matching parameter input).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  LABEL_ACTIONS,
  MAX_TEXT,
  PRIORITIES,
  TEXT_ACTIONS,
  defaultParams,
  i18nKey,
} from './timerUtils';

const props = defineProps({
  actionTypes: { type: Array, required: true },
  labelOptions: { type: Array, default: () => [] },
  agentOptions: { type: Array, default: () => [] },
  teamOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['remove']);

const action = defineModel({ type: Object, required: true });

const { t } = useI18n();

const setParams = params => {
  action.value = { ...action.value, action_params: params };
};

const actionName = computed({
  get: () => action.value.action_name,
  set: value => {
    action.value = {
      ...action.value,
      action_name: value,
      action_params: defaultParams(value),
    };
  },
});

const params = computed(() => action.value.action_params || []);

const firstParam = computed({
  get: () => params.value[0] ?? '',
  set: value => setParams([value]),
});

const listParam = computed({
  get: () => [...params.value],
  set: value => setParams([...value]),
});

const notifyConfig = computed(() => ({
  user_ids: [],
  team_ids: [],
  text: '',
  ...(params.value[0] || {}),
}));

const notifyField = field =>
  computed({
    get: () => {
      const value = notifyConfig.value[field];
      return Array.isArray(value) ? [...value] : value;
    },
    set: value => setParams([{ ...notifyConfig.value, [field]: value }]),
  });

const notifyUsers = notifyField('user_ids');
const notifyTeams = notifyField('team_ids');
const notifyText = notifyField('text');

const priorityOptions = computed(() =>
  PRIORITIES.map(value => ({
    value,
    label: t(`AIVIO_TIMERS.PRIORITIES.${i18nKey(value)}`),
  }))
);

// assign_agent also accepts «last responding agent» and «nobody» (backend agent_params).
const assignAgentOptions = computed(() => [
  {
    value: 'last_responding_agent',
    label: t('AIVIO_TIMERS.AGENT_SPECIAL.LAST_RESPONDING_AGENT'),
  },
  { value: 'nil', label: t('AIVIO_TIMERS.AGENT_SPECIAL.NIL') },
  ...props.agentOptions,
]);

const isLabelAction = computed(() => LABEL_ACTIONS.includes(actionName.value));
const isTextAction = computed(() => TEXT_ACTIONS.includes(actionName.value));

const hint = computed(() => {
  const key = `AIVIO_TIMERS.ACTION_HINTS.${i18nKey(actionName.value)}`;
  return ['send_message', 'notify', 'snooze_conversation'].includes(
    actionName.value
  )
    ? t(key)
    : '';
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between gap-2">
      <Select v-model="actionName" :options="actionTypes" />
      <Button
        type="button"
        icon="i-lucide-trash-2"
        variant="ghost"
        color="slate"
        size="sm"
        :title="t('AIVIO_TIMERS.FORM.REMOVE_ACTION')"
        @click="emit('remove')"
      />
    </div>

    <TagMultiSelectComboBox
      v-if="isLabelAction"
      v-model="listParam"
      :options="labelOptions"
      :placeholder="t('AIVIO_TIMERS.FORM.PICK_LABELS')"
      :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
    />

    <Select
      v-else-if="actionName === 'change_priority'"
      v-model="firstParam"
      :options="priorityOptions"
    />

    <ComboBox
      v-else-if="actionName === 'assign_agent'"
      v-model="firstParam"
      :options="assignAgentOptions"
      :placeholder="t('AIVIO_TIMERS.FORM.PICK_AGENT')"
      :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
    />

    <ComboBox
      v-else-if="actionName === 'assign_team'"
      v-model="firstParam"
      :options="teamOptions"
      :placeholder="t('AIVIO_TIMERS.FORM.PICK_TEAM')"
      :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
    />

    <TextArea
      v-else-if="isTextAction"
      v-model="firstParam"
      :max-length="MAX_TEXT"
      auto-height
      :placeholder="
        actionName === 'send_message'
          ? t('AIVIO_TIMERS.FORM.MESSAGE_PLACEHOLDER')
          : t('AIVIO_TIMERS.FORM.NOTE_PLACEHOLDER')
      "
    />

    <template v-else-if="actionName === 'notify'">
      <TagMultiSelectComboBox
        v-model="notifyUsers"
        :options="agentOptions"
        :placeholder="t('AIVIO_TIMERS.FORM.PICK_AGENTS')"
        :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
      />
      <TagMultiSelectComboBox
        v-model="notifyTeams"
        :options="teamOptions"
        :placeholder="t('AIVIO_TIMERS.FORM.PICK_TEAMS')"
        :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
      />
      <TextArea
        v-model="notifyText"
        :max-length="MAX_TEXT"
        auto-height
        :placeholder="t('AIVIO_TIMERS.FORM.NOTIFY_PLACEHOLDER')"
      />
    </template>

    <p v-if="hint" class="mb-0 text-xs text-n-slate-11">{{ hint }}</p>
  </div>
</template>
